import {
    Button,
    Flex,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    ModalHeader,
    Text,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { PageType } from "logger/page-tracking";
import { usePageTracking } from "logger/use-page-tracking";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    TwoFactorChallenge,
    TwoFactorChallengeStatus,
    TwoFactorVerificationStatus,
} from "types/two-factor";

const CODE_LENGTH = 6;

interface TwoFactorModalProps {
    challenge: TwoFactorChallenge;
    isSubmitting: boolean;
    onSubmitCode: (code: string) => void;
    onResend: () => void;
    onClose: () => void;
}

/**
 * Asks the guest for the code sent by sms. Shown when the api answered a finalize attempt with 428:
 * the booking is still held, so closing this is the guest's choice to abandon it rather than a
 * failure to report.
 */
export const TwoFactorModal = ({
    challenge,
    isSubmitting,
    onSubmitCode,
    onResend,
    onClose,
}: TwoFactorModalProps) => {
    const { t } = useTranslation();
    const [code, setCode] = useState("");
    const [secondsUntilResend, setSecondsUntilResend] = useState(challenge.retryAfterSeconds ?? 0);
    const [codeHasBeenSent, setCodeHasBeenSent] = useState(
        challenge.challengeStatus === TwoFactorChallengeStatus.Sent
    );

    usePageTracking({
        pageName: "TwoFactorConfirmation",
        pageType: PageType.Modal,
        autoTrack: true,
        metadata: {
            challengeStatus: challenge.challengeStatus ?? "",
            verificationStatus: challenge.verificationStatus ?? "",
        },
    });

    // A wrong code clears the field so the guest can retype without deleting six digits first.
    useEffect(() => {
        if (challenge.verificationStatus) setCode("");
    }, [challenge.verificationStatus, challenge.attemptsRemaining]);

    useEffect(() => {
        setSecondsUntilResend(challenge.retryAfterSeconds ?? 0);
        if (challenge.challengeStatus === TwoFactorChallengeStatus.Sent) setCodeHasBeenSent(true);
    }, [challenge.retryAfterSeconds, challenge.challengeStatus]);

    useEffect(() => {
        if (secondsUntilResend <= 0) return;
        const timer = setTimeout(() => setSecondsUntilResend((seconds) => seconds - 1), 1000);
        return () => clearTimeout(timer);
    }, [secondsUntilResend]);

    const message = () => {
        switch (challenge.challengeStatus) {
            case TwoFactorChallengeStatus.MissingMobile:
                return t("twoFactor.error.missingMobile");
            case TwoFactorChallengeStatus.SendLimitReached:
                return t("twoFactor.error.sendLimitReached");
            case TwoFactorChallengeStatus.DeliveryFailed:
                return t("twoFactor.error.deliveryFailed");
            default:
                return t("twoFactor.message");
        }
    };

    const error = () => {
        switch (challenge.verificationStatus) {
            case TwoFactorVerificationStatus.IncorrectCode:
                return challenge.attemptsRemaining
                    ? t("twoFactor.error.incorrectCodeWithAttempts", {
                          count: challenge.attemptsRemaining,
                      })
                    : t("twoFactor.error.incorrectCode");
            case TwoFactorVerificationStatus.Expired:
                return t("twoFactor.error.expired");
            case TwoFactorVerificationStatus.TooManyAttempts:
                return t("twoFactor.error.tooManyAttempts");
            case TwoFactorVerificationStatus.RecipientMismatch:
                return t("twoFactor.error.recipientMismatch");
            case TwoFactorVerificationStatus.NoChallenge:
                return t("twoFactor.error.noChallenge");
            default:
                return undefined;
        }
    };

    // Once a code is out it stays valid, so a refused resend (cooldown, send limit, delivery
    // failure) must not take the field away from a guest who already has a code to type.
    // CooldownActive counts on its own: the api only reports it while a code is still live, which
    // is what the guest sees after reloading the page mid-confirmation.
    const canEnterCode =
        challenge.challengeStatus === TwoFactorChallengeStatus.Sent ||
        challenge.challengeStatus === TwoFactorChallengeStatus.CooldownActive ||
        codeHasBeenSent;
    const verificationError = error();

    return (
        <Modal isOpen onClose={onClose} size="md" closeOnOverlayClick={false}>
            <ModalHeader
                display="flex"
                justifyContent="space-between"
                p={ThemeSpaceVariable.Medium}
            >
                <Text fontWeight="medium" fontSize={ThemeFontSizeVariable.XLarge}>
                    {t("twoFactor.title")}
                </Text>
                {/* The booking is only held, not lost, so abandoning it has to stay possible.
                    Without this the guest is stuck here: the overlay is deliberately inert so a
                    stray tap cannot discard a code they just received. */}
                <ModalCloseButton />
            </ModalHeader>
            <ModalBody
                display="flex"
                flexDirection="column"
                gap={ThemeSpaceVariable.Medium}
                px={[ThemeSpaceVariable.Medium, ThemeSpaceVariable.Large]}
            >
                <Text>{message()}</Text>

                {canEnterCode && (
                    <Flex direction="column" gap={ThemeSpaceVariable.Small}>
                        {/* Plain input for now; a dedicated pin input is a follow-up. */}
                        <Input
                            value={code}
                            onChange={(e) =>
                                setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))
                            }
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder={t("twoFactor.codePlaceholder")}
                            aria-label={t("twoFactor.codeLabel")}
                            aria-invalid={Boolean(verificationError)}
                            autoFocus
                        />
                        {verificationError && (
                            <Text color={ThemeColorVariable.Error}>{verificationError}</Text>
                        )}
                    </Flex>
                )}

                {!canEnterCode && verificationError && (
                    <Text color={ThemeColorVariable.Error}>{verificationError}</Text>
                )}
            </ModalBody>
            <ModalFooter
                display="flex"
                gap={ThemeSpaceVariable.Small}
                px={ThemeSpaceVariable.Medium}
                pt={ThemeSpaceVariable.Medium}
                pb={ThemeSpaceVariable.Large}
            >
                <Button
                    variant="secondary"
                    size="xl"
                    onClick={onResend}
                    disabled={isSubmitting || secondsUntilResend > 0}
                    data-track-click="two-factor-resend"
                >
                    {secondsUntilResend > 0
                        ? t("twoFactor.resendIn", { count: secondsUntilResend })
                        : t("twoFactor.resend")}
                </Button>
                <Button
                    variant="primary"
                    size="xl"
                    onClick={() => onSubmitCode(code)}
                    disabled={!canEnterCode || code.length < CODE_LENGTH || isSubmitting}
                    data-track-click="two-factor-confirm"
                >
                    {t("twoFactor.confirm")}
                </Button>
            </ModalFooter>
        </Modal>
    );
};
